import { AggregatedMonitoringStatus, AggregatedType } from 'features/tenants/models/aggregated-monitor-data';
import { Monitoring, TenantMonitoringType } from 'features/tenants/models/tenant';

type MonitoringItem = {
  type: AggregatedMonitoringStatus;
  count: number;
};

type MonitoringBar = {
  monitoringItems: MonitoringItem[];
};

export type MonitoringBarProps = {
  monitoring?: Monitoring[];
  aggregatedType: AggregatedType;
};

const useMonitoringBar = ({ monitoring, aggregatedType }: MonitoringBarProps): MonitoringBar => {
  const getTotalMonitoringCount = (items: Monitoring[]) => items.reduce((total, item) => total + item.count, 0);

  const getMonitoringCount = (items: Monitoring[], ...types: TenantMonitoringType[]) => {
    const relevantTypes = new Set(types);
    return items.reduce((total, item) => {
      if (relevantTypes.has(item.type)) {
        return total + item.count;
      }
      return total;
    }, 0);
  };

  const aggregatedMonitoring = (monitoringItems: Monitoring[], type: AggregatedType) => {
    const totalMonitoringCount = getTotalMonitoringCount(monitoringItems);
    if (totalMonitoringCount === 0) return [];

    switch (type) {
      case AggregatedType.Connectors:
        return [
          {
            type: AggregatedMonitoringStatus.Red,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Offline),
          },
          {
            type: AggregatedMonitoringStatus.Green,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Online),
          },
        ];
      case AggregatedType.Gateways:
        return [
          {
            type: AggregatedMonitoringStatus.Red,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Offline),
          },
          {
            type: AggregatedMonitoringStatus.Orange,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.PassiveGatewayOffline),
          },
          {
            type: AggregatedMonitoringStatus.Green,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Online),
          },
        ];
      case AggregatedType.Devices:
        return [
          {
            type: AggregatedMonitoringStatus.Red,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Offline),
          },
          {
            type: AggregatedMonitoringStatus.Orange,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.BatteryLow, TenantMonitoringType.BatteryCritical),
          },
          {
            type: AggregatedMonitoringStatus.Green,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Online),
          },
          {
            type: AggregatedMonitoringStatus.Grey,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.NotMonitored),
          },
        ];
      case AggregatedType.Systems:
        return [
          {
            type: AggregatedMonitoringStatus.Red,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Offline),
          },
          {
            type: AggregatedMonitoringStatus.Orange,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Warning),
          },
          {
            type: AggregatedMonitoringStatus.Green,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Online),
          },
        ];
      case AggregatedType.ConnectorsAndGateways:
        return [
          {
            type: AggregatedMonitoringStatus.Red,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Offline),
          },
          {
            type: AggregatedMonitoringStatus.Orange,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Warning, TenantMonitoringType.PassiveGatewayOffline),
          },
          {
            type: AggregatedMonitoringStatus.Green,
            count: getMonitoringCount(monitoringItems, TenantMonitoringType.Online),
          },
        ];
      default:
        return [];
    }
  };

  return {
    monitoringItems: aggregatedMonitoring(monitoring ?? [], aggregatedType),
  };
};

export default useMonitoringBar;
